import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, RadioGroup, FormControlLabel, Radio,
  TextField, Grid, Divider, CircularProgress, Alert,
} from '@mui/material';
import {
  CreditCard, MoneyOff, AccountBalance, Payment as PaymentIcon,
} from '@mui/icons-material';
import DialogHeader from '../Common/DialogHeader';
import { useAuth } from '../../contexts/AuthContext';
import usePaymentConfig, { providerAcceptsBarion } from '../../hooks/usePaymentConfig';
import paymentService from '../../services/paymentService';
import BarionPaymentOption from './BarionPaymentOption';

const PAYMENT_METHOD_LABELS = {
  ON_SITE_CARD: { label: 'Helyszínen bankkártyával', icon: <CreditCard /> },
  ON_SITE_CASH: { label: 'Helyszínen készpénzzel', icon: <MoneyOff /> },
  BANK_TRANSFER: { label: 'Online utalással (számlaszámra)', icon: <AccountBalance /> },
  BARION: { label: 'Online bankkártyás fizetés (Barion)', icon: <CreditCard /> },
};

const formatHUF = (n) => `${(n || 0).toLocaleString('hu-HU')} Ft`;

/**
 * 2-lépéses offline fizetési mód dialog.
 * props:
 *   open, onClose, bookings (array, provider adatokkal), paymentStep ('DEPOSIT'|'FULL'), onSuccess()
 */
const PaymentMethodDialog = ({ open, onClose, bookings = [], paymentStep = 'FULL', onSuccess }) => {
  const { user } = useAuth();
  const paymentConfig = usePaymentConfig();
  const [step, setStep] = useState(1); // 1: fizetési mód, 2: személyes adatok
  const [selectedMethod, setSelectedMethod] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    address: '', city: '', zipCode: '',
    wantsInvoice: false, invoiceName: '', invoiceAddress: '', invoiceTaxNumber: '',
  });

  useEffect(() => {
    if (user) {
      setForm(prev => ({
        ...prev,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
      }));
    }
    setStep(1);
    setSelectedMethod('');
    setError('');
    setResult(null);
  }, [open, user]);

  // Számítsuk ki a metszetet (csak az összes provider által elfogadott módok)
  const providerIds = [...new Set(bookings.map(b => b.providerId))];
  const isMultiProvider = providerIds.length > 1;

  const availableMethods = isMultiProvider ? [] :
    (bookings[0]?.provider?.paymentMethods || []).filter(m => m !== 'BARION');
  // Barion: több szolgáltatónál is működik, ha mindegyik fogad online fizetést
  const barionAvailable = paymentConfig.barionEnabled
    && bookings.length > 0
    && bookings.every(b => providerAcceptsBarion(b.provider));

  // Összeg kalkuláció
  const totalAmount = bookings.reduce((sum, b) => {
    if (paymentStep === 'DEPOSIT') return sum + parseFloat(b.depositAmount || 0);
    const deposit = b.paymentStatus === 'DEPOSIT_PAID' ? parseFloat(b.depositAmount || 0) : 0;
    return sum + Math.max(0, parseFloat(b.finalPrice || 0) - deposit);
  }, 0);

  // Bank account és reference IDs (BANK_TRANSFER esetén)
  const bankAccountNumber = !isMultiProvider ? bookings[0]?.provider?.bankAccountNumber : null;
  const transferReferenceIds = result?.data?.bookings || [];

  const handleNext = () => {
    if (!selectedMethod) {
      setError('Kérjük válasszon fizetési módot.');
      return;
    }
    setError('');
    setStep(2);
  };

  const handleBack = () => setStep(1);

  const handleSubmit = async () => {
    if (!form.firstName || !form.lastName || !form.email || !form.phone || !form.address || !form.city || !form.zipCode) {
      setError('Kérjük töltse ki az összes kötelező mezőt.');
      return;
    }
    setLoading(true);
    setError('');

    // Barion: átirányítás a fizetőoldalra, visszatérés a /payment/status oldalra
    if (selectedMethod === 'BARION') {
      try {
        const res = await paymentService.initiateBarionPayment(bookings.map(b => b.id), paymentStep, form);
        if (res.redirectUrl) {
          window.location.href = res.redirectUrl;
          return;
        }
        setError(res.message || 'Hiba történt a fizetés indításakor.');
      } catch (err) {
        setError(err.message || 'Hiba történt a fizetés indításakor.');
      }
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/initiate-offline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          bookingIds: bookings.map(b => b.id),
          selectedPaymentMethod: selectedMethod,
          paymentStep,
          checkoutDetails: form,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setResult(data);
        setStep(3);
      } else {
        setError(data.message || 'Hiba történt a fizetés indításakor.');
      }
    } catch (err) {
      setError('Hálózati hiba. Kérjük próbálja újra.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (step === 3 && onSuccess) onSuccess();
    onClose();
  };

  const tf = (field, label, extraProps = {}) => (
    <TextField
      fullWidth size="small" label={label}
      value={form[field]}
      onChange={(e) => setForm(prev => ({ ...prev, [field]: e.target.value }))}
      {...extraProps}
    />
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: '20px', background: 'rgba(245,241,230,0.97)' } }}>
      <DialogTitle sx={{ p: 0 }}>
        <DialogHeader icon={<PaymentIcon />}
          title={step === 3 ? 'Fizetés visszaigazolva' : step === 2 ? 'Személyes adatok' : 'Fizetési mód kiválasztása'}
          subtitle={step === 3 ? undefined :
            paymentStep === 'DEPOSIT'
              ? `Előleg fizetendő: ${formatHUF(totalAmount)}`
              : `Fizetendő összeg: ${formatHUF(totalAmount)}`}
        />
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* 1. lépés: Fizetési mód */}
        {step === 1 && (
          <>
            {isMultiProvider ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                Több szolgáltató foglalásait csak online (Barion) fizetéssel lehet egyszerre kiegyenlíteni.
              </Alert>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Válasszon fizetési módot. A szolgáltató az alábbi módokat fogadja el:
                </Typography>
                {availableMethods.length === 0 && !barionAvailable && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    A szolgáltató még nem állított be fizetési módot. Kérjük, vegye fel a kapcsolatot közvetlenül.
                  </Alert>
                )}
                <RadioGroup value={selectedMethod} onChange={(e) => setSelectedMethod(e.target.value)}>
                  {availableMethods.map(method => (
                    <FormControlLabel
                      key={method}
                      value={method}
                      control={<Radio />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {PAYMENT_METHOD_LABELS[method]?.icon}
                          <span>{PAYMENT_METHOD_LABELS[method]?.label || method}</span>
                        </Box>
                      }
                      sx={{ mb: 1, border: '1px solid', borderColor: selectedMethod === method ? 'primary.main' : 'divider',
                        borderRadius: '8px', px: 1.5, py: 0.5, mx: 0 }}
                    />
                  ))}
                </RadioGroup>
              </>
            )}

            <BarionPaymentOption
              enabled={paymentConfig.barionEnabled}
              available={barionAvailable}
              selected={selectedMethod === 'BARION'}
              onSelect={() => setSelectedMethod('BARION')}
            />

            {/* BANK_TRANSFER: referencia info előnézet */}
            {selectedMethod === 'BANK_TRANSFER' && bankAccountNumber && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2"><strong>Bankszámlaszám:</strong> {bankAccountNumber}</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  A megerősítés után egyedi közlemény azonosítót kap, amelyet az utalás közleményébe kell írni.
                </Typography>
              </Alert>
            )}
          </>
        )}

        {/* 2. lépés: Személyes adatok */}
        {step === 2 && (
          <>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>Személyes adatok</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>{tf('lastName', 'Vezetéknév *')}</Grid>
              <Grid item xs={6}>{tf('firstName', 'Keresztnév *')}</Grid>
              <Grid item xs={6}>{tf('email', 'Email *', { type: 'email' })}</Grid>
              <Grid item xs={6}>{tf('phone', 'Telefonszám *', { type: 'tel' })}</Grid>
            </Grid>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2.5, mb: 1.5 }}>Számlázási cím</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>{tf('address', 'Utca, házszám *')}</Grid>
              <Grid item xs={6}>{tf('city', 'Város *')}</Grid>
              <Grid item xs={6}>{tf('zipCode', 'Irányítószám *')}</Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Fizetési mód: <strong>{PAYMENT_METHOD_LABELS[selectedMethod]?.label || selectedMethod}</strong>
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} color="primary.main">
                {formatHUF(totalAmount)}
              </Typography>
            </Box>
          </>
        )}

        {/* 3. lépés: Siker */}
        {step === 3 && result && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h6" color="success.main" gutterBottom>
              Fizetési igény rögzítve!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Email visszaigazolást küldtünk a megadott email címre.
            </Typography>
            {result.data?.selectedPaymentMethod === 'BANK_TRANSFER' && result.data?.bookings?.length > 0 && (
              <Box sx={{ background: 'rgba(33,150,243,0.06)', border: '1px solid rgba(33,150,243,0.2)', borderRadius: '12px', p: 2, mt: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>Átutalási adatok:</Typography>
                <Typography variant="body2"><strong>Bankszámlaszám:</strong> {result.data.bankAccountNumber}</Typography>
                {result.data.bookings.map(b => (
                  <Box key={b.id} sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      Foglalás #{b.id} közleménye: <strong
                        style={{ fontFamily: 'monospace', background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>
                        {b.transferReferenceId}
                      </strong>
                    </Typography>
                  </Box>
                ))}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Kérjük, az utalás közleményébe a fenti azonosítót írja!
                </Typography>
              </Box>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Miután a fizetés megtörtént, kérjük, igazolja vissza a foglalás oldalán.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
        {step === 1 && (
          <>
            <Button onClick={handleClose} sx={{ color: 'text.secondary' }}>Mégse</Button>
            <Button variant="contained" onClick={handleNext} disabled={!selectedMethod}>
              Tovább
            </Button>
          </>
        )}
        {step === 2 && (
          <>
            <Button onClick={handleBack} sx={{ color: 'text.secondary' }}>Vissza</Button>
            <Button onClick={handleClose} sx={{ color: 'text.secondary' }}>Mégse</Button>
            <Button variant="contained" color="primary" onClick={handleSubmit} disabled={loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <PaymentIcon />}
              sx={{ background: 'linear-gradient(45deg, #2a5d32 0%, #1b331f 100%)' }}>
              {loading ? 'Feldolgozás...' : 'Megerősítés'}
            </Button>
          </>
        )}
        {step === 3 && (
          <Button variant="contained" onClick={handleClose} fullWidth>
            Bezárás
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PaymentMethodDialog;
